import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import {
  LayoutDashboard,
  Building2,
  Users,
  Target,
  History,
  LogOut,
  Menu,
  X,
  FileSignature,
  Car,
  Settings,
  TrendingUp,
  BarChart3,
  Download,
  UserCog,
  Wrench
} from "lucide-react";
import { useState } from "react";

const Sidebar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const getNavItems = () => {
    const items = [];

    if (user?.role === "admin" || user?.role === "hr") {
      items.push(
        { to: "/admin", icon: LayoutDashboard, label: "Tableau de bord" },
        { to: "/concessions", icon: Building2, label: "Concessions" },
        { to: "/managers", icon: UserCog, label: "Managers" },
        { to: "/sellers", icon: Users, label: "Vendeurs" },
        { to: "/objectives", icon: Target, label: "Objectifs" },
        { to: "/seasonality", icon: TrendingUp, label: "Saisonnalité" },
        { to: "/performance", icon: BarChart3, label: "Performance" },
        { to: "/history", icon: History, label: "Historique" },
        { to: "/export", icon: Download, label: "Export" },
        { to: "/settings", icon: Settings, label: "Paramètres" },
        { to: "/admin-tools", icon: Wrench, label: "Outils Admin" }
      );
    } else if (user?.role === "manager") {
      items.push(
        { to: "/manager", icon: LayoutDashboard, label: "Tableau de bord" },
        { to: "/sellers", icon: Users, label: "Mes vendeurs" },
        { to: "/objectives", icon: Target, label: "Objectifs" },
        { to: "/performance", icon: BarChart3, label: "Performance" },
        { to: "/history", icon: History, label: "Historique" },
        { to: "/settings", icon: Settings, label: "Paramètres" }
      );
    } else if (user?.role === "seller") {
      items.push(
        { to: "/seller", icon: LayoutDashboard, label: "Mes objectifs" },
        { to: "/settings", icon: Settings, label: "Paramètres" }
      );
    }

    return items;
  };

  const navItems = getNavItems();

  const SidebarContent = () => (
    <>
      {/* Logo */}
      <div className="p-6 border-b border-gray-800">
        <div className="flex items-start gap-3">
          <img 
            src="/logo-starter.png" 
            alt="Starter Logo" 
            className="w-11 h-11 object-contain mt-0.5"
          />
          <div className="leading-tight">
            <h1 className="font-bold text-white text-lg" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Starter
            </h1>
            <p className="text-xs text-gray-400 leading-tight">by Verbaere Performance<br/>Suite</p>
          </div>
        </div>
      </div>

      {/* User info */}
      <div className="p-4 border-b border-gray-800">
        <p className="text-sm text-gray-400">Connecté en tant que</p>
        <p className="text-white font-medium truncate">{user?.name}</p>
        <span className="inline-block mt-1 px-2 py-0.5 bg-gray-800 text-gray-300 text-xs rounded">
          {(user?.role === "admin" || user?.role === "hr") && "Administrateur"}
          {user?.role === "manager" && "Responsable"}
          {user?.role === "seller" && "Vendeur"}
        </span>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-1">
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            onClick={() => setMobileMenuOpen(false)}
            className={({ isActive }) =>
              `sidebar-link ${isActive ? "active" : ""}`
            }
          >
            <item.icon className="w-5 h-5" />
            <span>{item.label}</span>
          </NavLink>
        ))}
      </nav>

      {/* Logout */}
      <div className="p-4 border-t border-gray-800">
        <button
          onClick={handleLogout}
          className="sidebar-link w-full text-red-400 hover:text-red-300 hover:bg-red-900/20"
          data-testid="logout-btn"
        >
          <LogOut className="w-5 h-5" />
          <span>Déconnexion</span>
        </button>
      </div>
    </>
  );

  return (
    <>
      {/* Mobile menu button */}
      <button
        className="mobile-menu-btn fixed top-4 left-4 z-50 p-2 bg-[#212E38] text-white rounded-lg lg:hidden"
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        data-testid="mobile-menu-btn"
      >
        {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
      </button>

      {/* Mobile overlay */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar - Mobile */}
      <aside
        className={`sidebar fixed inset-y-0 left-0 z-40 w-64 transform transition-transform duration-200 ease-in-out lg:hidden ${
          mobileMenuOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex flex-col h-full">
          <SidebarContent />
        </div>
      </aside>

      {/* Sidebar - Desktop */}
      <aside className="sidebar hidden lg:flex lg:flex-col lg:w-64 lg:fixed lg:inset-y-0">
        <SidebarContent />
      </aside>
    </>
  );
};

export default Sidebar;
